//#region IMPORT
import axios from 'axios';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
/** Components Import */
import { Typography, IconButton, Box } from '@mui/material';
/** Icons Import */
import Lottie from 'lottie-react';
import AiAnimation from '../../Assets/animations/ai.json';
import { Capacitor } from '@capacitor/core';
import { VoiceRecorder } from 'capacitor-voice-recorder';
import { Loading } from '../MUIs';
//#endregion

const SpeechToText = forwardRef((props, ref) => {
  //#region 변수
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isProcessing, setIsProcessing] = useState(false); // 음성 처리 중 상태
  // const mediaRecorderRef = useRef(null); // MediaRecorder 참조
  // const streamRef = useRef(null); // 마이크 스트림 참조
  const timeoutRef = useRef(null); // 타이머 참조
  const ffmpeg = useMemo(() => new FFmpeg(), []);
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  // props 구조분해할당
  const { streamRef, mediaRecorderRef, setTtsAudio, onTranscriptionChange } =
    props;
  const config = useMemo(
    () => ({
      // encoding: 'LINEAR16',   // LINEAR16 인코딩 사용
      // sampleRateHertz: 16000, // 샘플레이트는 16000으로 설정 (Google의 권장값)
      languageCode: 'ko-KR', // 한국어 설정
    }),
    []
  );

  // 환경변수에서 API 키가 없으면 오류를 발생시킴
  if (!process.env.REACT_APP_GOOGLE_API_KEY) {
    throw new Error('REACT_APP_GOOGLE_API_KEY not found in the environment');
  }
  //#endregion

  //#region 데이터 변환 관련 함수들
  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      // 파일이 Blob 또는 File 타입인지 확인
      if (!(file instanceof Blob)) {
        reject(new Error('The provided value is not a Blob or File.'));
        return;
      }

      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  const convertMp4Base64ToWebm = useCallback(
    async (file) => {
      try {
        // file이 Blob 타입이 아니라면 Blob으로 변환 (예시로 Uint8Array 사용)
        if (!(file instanceof Blob)) {
          file = new Blob([file], { type: 'audio/mp4' });
        }

        const base64DataUrl = await fileToBase64(file);

        // base64DataUrl을 정상적으로 처리
        if (!base64DataUrl.includes(',')) {
          throw new Error('Invalid Base64 Data URL format');
        }

        const base64 = base64DataUrl.split(',')[1];
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        await ffmpeg.load();
        await ffmpeg.writeFile('input.mp4', bytes);
        await ffmpeg.exec(['-i', 'input.mp4', 'output.webm']);
        const outputWav = ffmpeg.readFile('output.webm');
        let result = outputWav.then((e) => {
          const wavBase64 = 'data:audio/webm;base64,' + uint8ArrayToBase64(e);
          return wavBase64.split(',')[1];
        });

        return result;
      } catch (error) {
        console.error('Error in conversion:', error);
      }
    },
    [ffmpeg]
  );

  function uint8ArrayToBase64(uint8Array) {
    let binary = '';
    const len = uint8Array.byteLength;

    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(uint8Array[i]);
    }

    return window.btoa(binary); // Base64로 인코딩
  }

  function base64ToBlob(base64, mimeType = 'audio/mp4') {
    // base64 문자열 앞에 'data:...'가 붙어있으면 제거
    const clean = base64.replace(/^data:.*;base64,/, '');
    const byteCharacters = atob(clean);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  // Google Speech-to-Text API로 오디오 데이터를 전송하는 함수
  const sendToGoogleSpeechAPI = useCallback(
    async (base64Audio) => {
      try {
        const response = await axios.post(
          `https://speech.googleapis.com/v1/speech:recognize?key=${apiKey}`,
          {
            config,
            audio: {
              content: base64Audio,
            },
          }
        );

        if (response.data.results && response.data.results.length > 0) {
          const newTranscript =
            response.data.results[0].alternatives[0].transcript;
          setTranscript(newTranscript);
          if (onTranscriptionChange) {
            onTranscriptionChange(newTranscript);
          }
        } else {
          const newTranscript =
            transcript === ''
              ? 'NULL1'
              : transcript === 'NULL1'
                ? 'NULL2'
                : 'NULL1';
          setTranscript(newTranscript);
          if (onTranscriptionChange) {
            onTranscriptionChange(newTranscript);
          }
        }
      } catch (error) {
        console.error(
          'Error with Google Speech-to-Text API:',
          error.response?.data || error.message
        );
      } finally {
        setIsProcessing(false); // API 호출 완료 후 처리 상태 해제
      }
    },
    [apiKey, config, transcript, onTranscriptionChange]
  );
  //#endregion

  //#region 녹음 시작/중지
  // 녹음을 시작하는 함수
  const startRecording = useCallback(async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        // 네이티브 녹음
        const canRecord = await VoiceRecorder.canDeviceVoiceRecord();
        if (!canRecord.value) {
          throw new Error('Cannot record on this device');
        }

        const hasPerm = await VoiceRecorder.hasAudioRecordingPermission();
        if (!hasPerm.value) {
          const permRes = await VoiceRecorder.requestAudioRecordingPermission();
          if (!permRes.value) {
            throw new Error('Microphone permission denied');
          }
        }

        // 녹음 시작
        await VoiceRecorder.startRecording();
        setTranscript('');
        setIsListening(true);
      } else {
        // 브라우저(PWA)
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        streamRef.current = stream;

        const options = { mimeType: 'audio/mp4' };
        const recorder = new MediaRecorder(stream, options);
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = async (event) => {
          clearTimeout(timeoutRef.current);
          const file = new Blob([event.data], { type: 'audio/mp3' });

          let webM = await convertMp4Base64ToWebm(event.data);
          setTtsAudio((prev) => [...prev, { gubun: 'webM', blob: file }]);

          await sendToGoogleSpeechAPI(webM);
        };

        recorder.start();
        setTranscript('');
        setIsListening(true);

        recorder.onstop = () => {
          stopRecording();
        };
      }
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  }, [
    streamRef,
    mediaRecorderRef,
    setTtsAudio,
    convertMp4Base64ToWebm,
    sendToGoogleSpeechAPI,
  ]);

  // 녹음을 중지하는 함수
  const stopRecording = useCallback(async () => {
    setIsListening(false); // 녹음 중 상태 해제
    setIsProcessing(true); // 처리 시작

    if (Capacitor.isNativePlatform()) {
      try {
        const data = await VoiceRecorder.stopRecording();
        const base64Data = data.value.recordDataBase64; // data.value.recordDataBase64 → 녹음 데이터

        const fileBlob = await base64ToBlob(base64Data, 'audio/mp4'); // mimeType 확인 필요
        const webM = await convertMp4Base64ToWebm(fileBlob);
        setTtsAudio((prev) => [...prev, { gubun: 'webM', blob: fileBlob }]);

        await sendToGoogleSpeechAPI(webM);
      } catch (err) {
        console.error('Native stopRecording error:', err);
        setIsProcessing(false); // 에러 시 처리 상태 해제
      }
    } else {
      // 웹(PWA)
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        mediaRecorderRef.current.stop(); // 녹음 중지
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop()); // 마이크 스트림 해제
      }
    }
  }, [
    streamRef,
    mediaRecorderRef,
    setTtsAudio,
    convertMp4Base64ToWebm,
    sendToGoogleSpeechAPI,
  ]);
  //#endregion

  // 화면을 나갈 때(언마운트) 마이크/녹음 해제 → 다시 들어왔을 때 ALREADY_RECORDING 방지
  useEffect(() => {
    return () => {
      if (Capacitor.isNativePlatform()) {
        VoiceRecorder.stopRecording().catch(() => {});
      } else {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== 'inactive'
        ) {
          mediaRecorderRef.current.stop();
        }
        streamRef.current?.getTracks().forEach((track) => track.stop());
      }
    };
  }, [streamRef, mediaRecorderRef]);

  // useImperativeHandle을 사용하여 부모 컴포넌트에서 접근할 수 있는 메서드를 정의합니다.
  useImperativeHandle(ref, () => ({
    startRecording,
    stopRecording,
    isRecording: () => isListening, // recording 상태를 반환하는 함수
  }));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '5px',
        position: 'relative',
      }}
    >
      <IconButton
        sx={{
          height: 100,
          opacity: isProcessing ? 0.5 : 1, // 처리 중일 때 버튼 비활성화 표시
        }}
        onClick={isListening ? stopRecording : startRecording}
        disabled={isProcessing} // 처리 중일 때 버튼 비활성화
      >
        <Lottie
          animationData={AiAnimation}
          loop
          autoplay
          style={{
            width: isListening ? 100 : 90,
            height: isListening ? 100 : 90,
            transition: 'all 0.3s ease-in-out',
            transform: isListening ? 'scale(1.1)' : 'scale(1.0)',
          }}
        />
      </IconButton>

      {/* 로딩 표시 */}
      {isProcessing && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
          }}
        >
          <Loading open={true} />
        </Box>
      )}

      <Typography
        sx={{
          color: '#F0F0F0',
          minHeight: '20px',
          textAlign: 'center',
          px: 2,
          maxWidth: 340,
          lineHeight: 1.4,
          whiteSpace: 'pre-line',
          wordBreak: 'keep-all',
        }}
      >
        {isListening
          ? '녹음 중입니다.\n대답 후 버튼을 다시 눌러 주세요.'
          : isProcessing
            ? '음성을 처리 중입니다...'
            : '\u00A0'}
      </Typography>
    </Box>
  );
});

export default SpeechToText;
